import React, { useState, useContext } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import { Card, CardMedia } from "@mui/material";
import Carousel from "react-material-ui-carousel";
import { useTheme } from "./ThemeContext";
import { AuthContext } from "../context/AuthContext";
import useVote from "../hooks/useVote";
import { getImageUrl } from "../utils/imageUrl";
import Utils from "../utils/Utils";
import { useNavigate, useLocation } from "react-router-dom";
import { DeviceContext } from "../context/DeviceContext";
import {
  LuArrowBigUp,
  LuArrowBigDown,
  LuAward,
  LuShare2,
  LuTrendingUp,
  LuChevronLeft,
  LuChevronDown,
  LuChevronUp,
} from "react-icons/lu";
import ShareDialog from "./ShareDialog";
import CommentSection from "./CommentSection";

// ── Helpers ────────────────────────────────────────────────────────────────────
const getPosType = (p) => (p.type || p.positionType || "").toLowerCase();

const formatPositionType = (type) => {
  if (!type) return "Unknown";
  return type
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// ── Position card ──────────────────────────────────────────────────────────────
const PositionCard = ({ position }) => {
  const posType = getPosType(position);
  const status = position.status || "OPEN";

  const entryPrice = position.entryPrice
    ? parseFloat(position.entryPrice)
    : null;
  const exitPrice = position.exitPrice ? parseFloat(position.exitPrice) : null;
  const currentPrice = position.currentPrice
    ? parseFloat(position.currentPrice)
    : null;
  const qty = position.quantity != null ? parseFloat(position.quantity) : null;

  const comparePrice = currentPrice ?? exitPrice ?? null;
  const pnl =
    entryPrice != null && comparePrice != null && qty != null
      ? (comparePrice - entryPrice) * qty
      : null;
  const pnlPct =
    entryPrice != null && comparePrice != null
      ? ((comparePrice - entryPrice) / entryPrice) * 100
      : null;
  const isProfit = pnl != null ? pnl >= 0 : null;

  const value = comparePrice != null && qty != null ? comparePrice * qty : null;

  const fmt = (n) =>
    n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const qtyLabel =
    qty != null
      ? posType.includes("crypto")
        ? `${qty} ${position.ticker}`
        : posType.includes("call") || posType.includes("put")
          ? `${qty} contract${qty !== 1 ? "s" : ""}`
          : `${qty} share${qty !== 1 ? "s" : ""}`
      : null;

  // Stat columns: show what we have
  const stats = [
    entryPrice != null
      ? { label: "Entry", value: `$${fmt(entryPrice)}`, color: "#d1d5db" }
      : null,
    currentPrice != null
      ? { label: "Current", value: `$${fmt(currentPrice)}`, color: "#d1d5db" }
      : null,
    exitPrice != null && !currentPrice
      ? { label: "Exit", value: `$${fmt(exitPrice)}`, color: "#d1d5db" }
      : null,
    value != null
      ? { label: "Value", value: `$${fmt(value)}`, color: "#d1d5db" }
      : null,
    pnl != null
      ? {
          label: "P/L",
          value: pnl >= 0 ? `+$${fmt(pnl)}` : `-$${fmt(Math.abs(pnl))}`,
          color: isProfit ? "#4ade80" : "#f87171",
        }
      : null,
    position.strikePrice
      ? {
          label: "Strike",
          value: `$${fmt(parseFloat(position.strikePrice))}`,
          color: "#d1d5db",
        }
      : null,
    position.expirationDate
      ? {
          label: "Exp",
          value: new Date(position.expirationDate).toLocaleDateString(),
          color: "#d1d5db",
        }
      : null,
  ].filter(Boolean);

  return (
    <Box
      sx={{
        backgroundColor: "rgba(26,31,38,0.4)",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: "8px",
        overflow: "hidden",
        transition: "border-color 0.2s",
        "&:hover": { borderColor: "rgba(34,197,94,0.3)" },
      }}>
      {/* Compact header row */}
      <Box
        sx={{
          p: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Gradient icon box */}
          <Box
            sx={{
              width: 32,
              height: 32,
              background:
                "linear-gradient(135deg, rgba(34,197,94,0.2) 0%, rgba(16,185,129,0.2) 100%)",
              borderRadius: "6px",
              border: "1px solid rgba(34,197,94,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}>
            <LuTrendingUp size={16} style={{ color: "#4ade80" }} />
          </Box>

          <Box>
            {/* Ticker + action + status row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                mb: "2px",
              }}>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: "#f3f4f6",
                  lineHeight: 1,
                }}>
                {position.ticker}
              </Typography>
              <Box
                sx={{
                  px: "8px",
                  py: "2px",
                  backgroundColor: "rgba(22,163,74,0.9)",
                  borderRadius: "4px",
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  color: "#fff",
                  fontFamily: "Inter, sans-serif",
                  lineHeight: "16px",
                }}>
                {formatPositionType(posType)}
              </Box>
              <Box
                sx={{
                  px: "8px",
                  py: "2px",
                  backgroundColor: "#3b82f6",
                  borderRadius: "4px",
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  color: "#fff",
                  fontFamily: "Inter, sans-serif",
                  lineHeight: "16px",
                }}>
                {status}
              </Box>
            </Box>
            {/* Quantity */}
            {qtyLabel && (
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.875rem",
                  color: "#6b7280",
                  lineHeight: 1,
                }}>
                {qtyLabel}
              </Typography>
            )}
          </Box>
        </Box>

        {/* P&L % badge */}
        {pnlPct != null && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              px: "10px",
              py: "4px",
              borderRadius: "6px",
              backgroundColor: isProfit
                ? "rgba(34,197,94,0.2)"
                : "rgba(239,68,68,0.2)",
              border: `1px solid ${isProfit ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
            }}>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "0.875rem",
                fontWeight: 700,
                color: isProfit ? "#4ade80" : "#f87171",
              }}>
              {pnlPct >= 0 ? "+" : ""}
              {pnlPct.toFixed(1)}%
            </Typography>
          </Box>
        )}
      </Box>

      {/* Stats grid */}
      {stats.length > 0 && (
        <Box
          sx={{
            px: "12px",
            pb: "12px",
            display: "grid",
            gridTemplateColumns: `repeat(${Math.min(stats.length, 4)}, 1fr)`,
            gap: "12px",
          }}>
          {stats.map((stat) => (
            <Box key={stat.label}>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.875rem",
                  color: "#6b7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  mb: "2px",
                }}>
                {stat.label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: stat.color,
                }}>
                {stat.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ── Positions section ──────────────────────────────────────────────────────────
const PositionsSection = ({ positions }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box sx={{ mb: "8px" }}>
      {/* Header toggle */}
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: "16px",
          cursor: "pointer",
          transition: "background-color 0.15s",
          "&:hover": { backgroundColor: "rgba(98,244,129,0.02)" },
        }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <LuTrendingUp size={18} style={{ color: "#4ade80" }} />
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "1rem",
              fontWeight: 700,
              color: "#f3f4f6",
            }}>
            Active Positions
          </Typography>
          <Box
            sx={{
              px: "10px",
              py: "2px",
              backgroundColor: "rgba(34,197,94,0.2)",
              border: "1px solid rgba(34,197,94,0.3)",
              borderRadius: "9999px",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#4ade80",
              fontFamily: "Inter, sans-serif",
            }}>
            {positions.length}
          </Box>
        </Box>
        {expanded ? (
          <LuChevronUp size={18} style={{ color: "#9ca3af" }} />
        ) : (
          <LuChevronDown size={18} style={{ color: "#9ca3af" }} />
        )}
      </Box>

      {/* Cards */}
      <Collapse in={expanded}>
        <Box
          sx={{
            px: "16px",
            pb: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}>
          {positions.map((pos, i) => (
            <PositionCard key={pos.positionId ?? i} position={pos} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
const PostInPostPage = ({ post, comments, updateComments, updatePosts }) => {
  useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  useContext(DeviceContext);

  const handleBack = () => {
    if (location.key === "default") {
      navigate("/");
    } else {
      navigate(-1);
    }
  };
  const { isLoggedIn, user } = useContext(AuthContext);

  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [commentCount, setCommentCount] = useState(post.commentCount);
  const [upvoted, setUpvoted] = useState(false);
  const [downvoted, setDownvoted] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const { handleVote, upvotes, downvotes } = useVote(post, updatePosts);
  const formattedTime = Utils.getFormattedTime(post.createdAt);

  const handleUpvote = () => {
    if (downvoted) setDownvoted(false);
    setUpvoted((v) => !v);
    handleVote(post, "UPVOTE");
  };
  const handleDownvote = () => {
    if (upvoted) setUpvoted(false);
    setDownvoted((v) => !v);
    handleVote(post, "DOWNVOTE");
  };

  const handleCommentSubmit = async () => {
    if (!isLoggedIn) {
      alert("You must be logged in to comment.");
      return;
    }
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/comments`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({
            userId: user.id,
            postId: post.postId,
            content: comment,
            parentCommentId: null,
          }),
        },
      );
      if (res.ok) {
        const data = await res.json();
        const newComment = {
          commentId: data.commentId,
          userName: user.username,
          content: comment,
          upvotes: 0,
          downvotes: 0,
          createdAt: data.createdAt,
        };
        const updated = [...(comments || []), newComment];
        updateComments(updated);
        setCommentCount((c) => c + 1);
        setComment("");
      }
    } catch (err) {
      console.error("Comment failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ pb: 2 }}>
      {/* Back to feed */}
      <Box
        onClick={handleBack}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          cursor: "pointer",
          mb: "24px",
          color: "#c5c9d1",
          fontSize: "0.875rem",
          fontFamily: "Inter, sans-serif",
          transition: "color 0.15s",
          "&:hover": { color: "#4ade80" },
        }}>
        <LuChevronLeft size={16} />
        Back to feed
      </Box>

      {/* ── Post content ── */}
      <Box>
        {/* ── Header ── */}
        <Box sx={{ pb: "16px" }}>
          {/* Time + author */}
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "0.875rem",
              color: "#9ca3af",
              mb: "12px",
            }}>
            {formattedTime} by{" "}
            <Box
              component="span"
              onClick={() => navigate(`/profile/${post.userName}`)}
              sx={{
                fontWeight: 700,
                color: "#62f481",
                cursor: "pointer",
                "&:hover": { opacity: 0.8 },
              }}>
              {post.userName}
            </Box>
          </Typography>

          {/* Tags */}
          {post.tags?.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                mb: "12px",
              }}>
              {post.tags.map((tag, i) => (
                <Box
                  key={i}
                  sx={{
                    px: "8px",
                    py: "4px",
                    backgroundColor: "rgba(0,201,80,0.1)",
                    border: "1px solid rgba(0,201,80,0.2)",
                    borderRadius: "9999px",
                    color: "#05df72",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    fontFamily: "Inter, sans-serif",
                  }}>
                  {tag}
                </Box>
              ))}
            </Box>
          )}

          {/* Title */}
          <Typography
            component="h1"
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: { xs: "1rem", md: "1.4rem" },
              fontWeight: 700,
              color: "#f3f4f6",
              lineHeight: 1.3,
              letterSpacing: "-0.02em",
            }}>
            {post.title}
          </Typography>
        </Box>

        {/* ── Image ── */}
        {post.images?.length > 0 && (
          <Carousel swipe={true} height="18rem" autoPlay={false}>
            {post.images.map((image) => (
              <Card key={image.imageId} sx={{ backgroundColor: "#293b4e" }}>
                <CardMedia
                  component="img"
                  sx={{ height: "18rem", objectFit: "contain" }}
                  image={getImageUrl(image.url)}
                  alt={`image-${image.imageId}`}
                />
              </Card>
            ))}
          </Carousel>
        )}

        {/* ── Content ── */}
        {post.content && (
          <Box sx={{ p: "0px" }}>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "0.875rem",
                color: "#e5e7ec",
                lineHeight: "1.25rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}>
              {post.content}
            </Typography>
          </Box>
        )}

        {/* ── Positions ── */}
        {post.positions?.length > 0 && (
          <PositionsSection positions={post.positions} />
        )}

        {/* ── Interaction bar ── */}
        <Box
          sx={{
            py: "12px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}>
          {/* Upvote */}
          <Box
            onClick={handleUpvote}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              px: "12px",
              py: "8px",
              borderRadius: "8px",
              backgroundColor: "rgba(0,201,80,0.1)",
              color: "#05df72",
              cursor: "pointer",
              transition: "background-color 0.15s",
              "&:hover": { backgroundColor: "rgba(0,201,80,0.15)" },
            }}>
            <LuArrowBigUp
              size={16}
              style={{ fill: upvoted ? "#05df72" : "none" }}
            />
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "inherit",
                lineHeight: 1,
              }}>
              {upvotes}
            </Typography>
          </Box>

          {/* Downvote */}
          <Box
            onClick={handleDownvote}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              px: "12px",
              py: "8px",
              borderRadius: "8px",
              backgroundColor: "rgba(251,44,54,0.1)",
              color: "#ff6467",
              cursor: "pointer",
              transition: "background-color 0.15s",
              "&:hover": { backgroundColor: "rgba(251,44,54,0.15)" },
            }}>
            <LuArrowBigDown
              size={16}
              style={{ fill: downvoted ? "#ff6467" : "none" }}
            />
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "inherit",
                lineHeight: 1,
              }}>
              {downvotes}
            </Typography>
          </Box>

          {/* Award — commented out until backend is implemented
          <Box
            sx={{
              display: "flex", alignItems: "center", gap: "6px",
              px: "12px", py: "8px", borderRadius: "8px",
              backgroundColor: "rgba(240,177,0,0.1)",
              color: "#fdc700", cursor: "pointer",
              transition: "background-color 0.15s",
              "&:hover": { backgroundColor: "rgba(240,177,0,0.15)" },
            }}
          >
            <LuAward size={16} />
            <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 500, color: "inherit", lineHeight: 1 }}>
              {post.awards ?? 0}
            </Typography>
          </Box>
          */}

          {/* Share */}
          <Box
            onClick={() => {
              setShareOpen(true);
            }}
            sx={{
              p: "8px",
              borderRadius: "8px",
              cursor: "pointer",
              color: "#9ca3af",
              transition: "background-color 0.15s",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.05)" },
            }}>
            <LuShare2 size={16} />
          </Box>
        </Box>

        {/* ── Comment input ── */}
        <Box sx={{ py: "16px" }}>
          <Box sx={{ position: "relative" }}>
            <textarea
              placeholder="Add Comment..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={submitting}
              rows={3}
              style={{
                width: "100%",
                backgroundColor: "transparent",
                border: "1px solid #ffffffb7",
                borderRadius: "4px",
                padding: "10px 12px",
                fontSize: "0.875rem",
                fontFamily: "Inter, sans-serif",
                color: "#e6e9ee",
                outline: "none",
                boxSizing: "border-box",
                transition: "border-color 0.15s",
                resize: "vertical",
                lineHeight: 1.5,
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#22c55e";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#ccccccbf";
              }}
            />
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", mt: "8px" }}>
              <Box
                onClick={!submitting ? handleCommentSubmit : undefined}
                sx={{
                  px: "16px",
                  py: "6px",
                  background: "linear-gradient(to right, #22c55e, #059669)",
                  color: "#fff",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  borderRadius: "6px",
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.6 : 1,
                  transition: "all 0.15s",
                  fontFamily: "Inter, sans-serif",
                  userSelect: "none",
                  "&:hover": { boxShadow: "0 0 15px rgba(34,197,94,0.2)" },
                }}>
                {submitting ? "Posting…" : "Post"}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ── Comments header ── */}
        <Box
          sx={{
            pb: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "1rem",
              fontWeight: 700,
              color: "#ffffff",
            }}>
            Comments
          </Typography>
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "1rem",
              color: "#dfd8d8",
            }}>
            {commentCount ?? 0} comments
          </Typography>
        </Box>

        {/* ── Comments ── */}
        <CommentSection
          comments={comments}
          updateComments={updateComments}
          post={post}
        />
      </Box>

      {/* Share dialog */}
      <ShareDialog
        open={shareOpen}
        url={window.location.origin + window.location.pathname}
        handleClose={() => setShareOpen(false)}
        handleCopy={() => {
          navigator.clipboard.writeText(
            window.location.origin + window.location.pathname,
          );
          setShareOpen(false);
        }}
      />
    </Box>
  );
};

export default PostInPostPage;
